import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, fmtError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui-kit";
import { Loader, EmptyState } from "@/components/Loader";
import { TrustBadge, STATUS_META } from "@/components/TrustBadge";
import { ArrowLeft, ShieldCheck, TrendingUp, Clock, History, BadgeCheck, Loader2, Info, Phone, Upload, FileText, X, Check } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }); } catch { return ""; } };
const ID_TYPES = ["Aadhaar", "Voter ID", "Driving Licence", "Passport", "Utility Bill", "Rent Agreement"];

function VerificationModal({ phoneVerified, onClose, onDone }) {
  const { refreshUser } = useAuth();
  const [stage, setStage] = useState(phoneVerified ? "docs" : "phone");
  const [busy, setBusy] = useState(false);
  // phone
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  // docs
  const [idType, setIdType] = useState(ID_TYPES[0]);
  const [docs, setDocs] = useState([]);
  const [note, setNote] = useState("");
  const fileRef = useRef(null);

  const sendPhone = async () => {
    setBusy(true);
    try { const { data } = await api.post("/me/phone/request-otp", { phone }); setOtpSent(true); if (data.dev_otp) toast.info(`Verification code: ${data.dev_otp}`, { duration: 8000 }); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const confirmPhone = async () => {
    setBusy(true);
    try { await api.post("/me/phone/confirm-otp", { phone, code }); toast.success("Phone verified"); await refreshUser(); setStage("docs"); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload/document", fd, { headers: { "Content-Type": "multipart/form-data" } }); setDocs((d) => [...d, { type: idType, url: data.url, name: file.name }]); toast.success("Document uploaded"); }
    catch (err) { toast.error(fmtError(err.response?.data?.detail)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const submit = async () => {
    if (docs.length === 0) { toast.error("Upload at least one document"); return; }
    setBusy(true);
    try { await api.post("/verification/request", { note, id_type: idType, document_urls: docs.map((d) => d.url) }); toast.success("Verification request submitted"); await refreshUser(); onDone(); }
    catch (e) { toast.error(fmtError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="verification-modal" onClick={onClose}>
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold font-head">Get verified</h3>
          <button onClick={onClose} data-testid="verif-modal-close" className="w-9 h-9 rounded-full bg-[#111827]/5 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <span className={`flex-1 flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl ${stage === "phone" ? "bg-[#F97316] text-white" : "bg-[#16A34A]/10 text-[#16A34A]"}`}>{stage !== "phone" && <Check className="w-3.5 h-3.5" />} 1. Verify phone</span>
          <span className={`flex-1 flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl ${stage === "docs" ? "bg-[#F97316] text-white" : "bg-[#111827]/5 text-[#111827]/50"}`}>2. Upload proof</span>
        </div>

        {stage === "phone" && (
          <div className="ll-fade-up">
            <p className="text-sm text-[#111827]/60 mb-3">Verify a mobile number to strengthen your identity before requesting resident verification.</p>
            <input data-testid="verif-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" className="w-full bg-white border-2 border-[#111827]/10 rounded-xl p-3.5 outline-none focus:border-[#F97316] mb-3" />
            {otpSent && <input data-testid="verif-otp-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} maxLength={6} placeholder="Enter 6-digit OTP" className="w-full bg-white border-2 border-[#111827]/10 rounded-xl p-3.5 outline-none focus:border-[#F97316] mb-3" />}
            {!otpSent ? (
              <button data-testid="verif-send-otp" onClick={sendPhone} disabled={busy || phone.length < 10} className="w-full bg-[#F97316] text-white rounded-2xl py-3.5 font-bold flex items-center justify-center gap-2 min-h-[52px] disabled:opacity-60">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Phone className="w-4 h-4" /> Send OTP</>}</button>
            ) : (
              <button data-testid="verif-confirm-otp" onClick={confirmPhone} disabled={busy || code.length < 6} className="w-full bg-[#16A34A] text-white rounded-2xl py-3.5 font-bold flex items-center justify-center gap-2 min-h-[52px] disabled:opacity-60">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-4 h-4" /> Verify phone</>}</button>
            )}
          </div>
        )}

        {stage === "docs" && (
          <div className="ll-fade-up">
            <label className="text-sm font-semibold mb-1.5 block">ID / address proof type</label>
            <select data-testid="verif-id-type" value={idType} onChange={(e) => setIdType(e.target.value)} className="w-full bg-white border-2 border-[#111827]/10 rounded-xl p-3.5 outline-none focus:border-[#F97316] mb-3">
              {ID_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={uploadDoc} className="hidden" data-testid="verif-doc-file" />
            <button data-testid="verif-upload-btn" onClick={() => fileRef.current?.click()} disabled={busy} className="w-full border-2 border-dashed border-[#F97316]/40 text-[#F97316] rounded-2xl py-4 font-bold flex items-center justify-center gap-2 mb-3">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Upload className="w-4 h-4" /> Upload document (image / PDF)</>}</button>
            {docs.length > 0 && (
              <div className="space-y-2 mb-3" data-testid="verif-doc-list">
                {docs.map((d, i) => (
                  <div key={i} className="flex items-center gap-2 bg-[#16A34A]/8 rounded-xl px-3 py-2 text-sm">
                    <FileText className="w-4 h-4 text-[#16A34A] shrink-0" />
                    <span className="flex-1 truncate">{d.type} · {d.name}</span>
                    <button onClick={() => setDocs(docs.filter((_, x) => x !== i))} className="text-[#DC2626]"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
            <textarea data-testid="verif-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note for the admin (e.g. how long you've lived here)" className="w-full bg-white border-2 border-[#111827]/10 rounded-xl p-3.5 outline-none focus:border-[#F97316] mb-3 text-sm" />
            <button data-testid="verif-submit" onClick={submit} disabled={busy || docs.length === 0} className="w-full bg-[#F97316] text-white rounded-2xl py-4 font-bold flex items-center justify-center gap-2 min-h-[56px] disabled:opacity-60">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ShieldCheck className="w-5 h-5" /> Submit for review</>}</button>
            <p className="text-[11px] text-[#111827]/40 mt-3 text-center">Documents are shared only with your community admin for review.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Trust() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = () => api.get("/me/trust").then((r) => setData(r.data)).catch(() => setData({}));
  useEffect(() => { load(); }, []);

  if (!data) return <Loader />;
  const st = STATUS_META[data.account_status] || STATUS_META.active;
  const vs = data.verification_status;

  return (
    <div className="ll-fade-up" data-testid="trust-screen">
      <div className="px-5 pt-8 pb-2 flex items-center gap-3">
        <button onClick={() => navigate("/profile")} data-testid="trust-back" className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-2xl font-bold font-head tracking-tight">Trust & Verification</h1>
      </div>

      <div className="px-5 mt-3">
        <div className="bg-[#111827] text-white rounded-3xl p-6 relative overflow-hidden" data-testid="trust-hero">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full" style={{ background: `${data.badge_color}44`, filter: "blur(30px)" }} />
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-white/50">Trust score</p>
              <p className="text-6xl font-black font-head" data-testid="trust-score">{data.trust_score}</p>
              <p className="text-white/60 text-sm">out of 100</p>
            </div>
            <div className="text-right">
              <TrustBadge badge={data.badge} testId="trust-badge" />
              <p className="text-[11px] text-white/40 mt-2 max-w-[140px]">Based on verified actions, not popularity.</p>
            </div>
          </div>
          <div className="relative h-2.5 rounded-full bg-white/15 mt-4 overflow-hidden">
            <motion.div className="h-full rounded-full" style={{ background: data.badge_color }} initial={{ width: 0 }} animate={{ width: `${data.trust_score}%` }} transition={{ duration: 1 }} />
          </div>
        </div>

        {/* Phone verification status */}
        <Card className="mt-4" data-testid="phone-verify-card">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold flex items-center gap-2"><Phone className="w-4 h-4 text-[#F97316]" /> Phone {data.phone ? `· ${data.phone}` : ""}</span>
            {data.phone_verified
              ? <span className="text-xs font-bold text-[#16A34A] flex items-center gap-1" data-testid="phone-verified-badge"><BadgeCheck className="w-4 h-4" /> Verified</span>
              : <span className="text-xs font-bold text-[#92400e] bg-[#F59E0B]/15 px-2 py-1 rounded-full">Not verified</span>}
          </div>
        </Card>

        <Card className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold flex items-center gap-2"><BadgeCheck className="w-4 h-4" style={{ color: st.color }} /> Account: <span style={{ color: st.color }}>{st.label}</span></span>
            <span className="text-sm font-semibold capitalize" style={{ color: vs === "verified" ? "#16A34A" : vs === "pending" ? "#F59E0B" : "#6b7280" }}>{vs}</span>
          </div>
          {vs === "verified" ? (
            <div className="flex items-center gap-2 bg-[#16A34A]/10 text-[#16A34A] rounded-xl p-3 font-semibold"><ShieldCheck className="w-5 h-5" /> You are a verified resident.</div>
          ) : vs === "pending" ? (
            <div className="flex items-center gap-2 bg-[#F59E0B]/10 text-[#92400e] rounded-xl p-3 font-semibold"><Clock className="w-5 h-5" /> Verification pending community admin review.</div>
          ) : (
            <button data-testid="request-verification" onClick={() => setShowModal(true)} className="w-full bg-[#F97316] text-white rounded-2xl py-3.5 font-bold flex items-center justify-center gap-2 min-h-[52px]"><ShieldCheck className="w-5 h-5" /> Request verification</button>
          )}
        </Card>

        <Card className="mt-4">
          <h3 className="text-lg font-bold font-head flex items-center gap-2 mb-4"><TrendingUp className="w-5 h-5 text-[#F97316]" /> Score breakdown</h3>
          <div className="space-y-4">
            {data.breakdown.map((b, i) => (
              <div key={i}>
                <div className="flex items-center justify-between text-sm mb-1"><span className="font-semibold">{b.label}</span><span className={`font-bold ${b.points < 0 ? "text-[#DC2626]" : "text-[#111827]"}`}>{b.points > 0 ? "+" : ""}{b.points}{b.max ? ` / ${b.max}` : ""}</span></div>
                {b.max > 0 && <div className="h-2 rounded-full bg-[#111827]/10 overflow-hidden"><div className="h-full rounded-full bg-[#F97316]" style={{ width: `${Math.min(100, (b.points / b.max) * 100)}%` }} /></div>}
                <p className="text-xs text-[#111827]/50 mt-1">{b.detail}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#111827]/40 mt-4 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Trust scoring reflects verified activity within your community.</p>
        </Card>

        <Card className="mt-4 mb-2">
          <h3 className="text-lg font-bold font-head flex items-center gap-2 mb-3"><History className="w-5 h-5 text-[#F97316]" /> Emergency response history</h3>
          {data.response_history.length === 0 ? (
            <EmptyState icon={History} title="No responses yet" subtitle="When you accept an emergency as a responder, it appears here." />
          ) : (
            <div className="space-y-2">
              {data.response_history.map((h) => (
                <div key={h.id} className="flex items-center gap-3 py-2 border-b border-[#111827]/5 last:border-0">
                  <div className="w-10 h-10 rounded-xl bg-[#C4B5FD]/25 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-[#111827]/60" /></div>
                  <div className="flex-1"><p className="font-semibold text-sm">{h.type_label}</p><p className="text-xs text-[#111827]/50">{h.community_name} · {fmtDate(h.date)}</p></div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${h.outcome === "Resolved" ? "bg-[#16A34A]/10 text-[#16A34A]" : "bg-[#F97316]/10 text-[#F97316]"}`}>{h.outcome}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {showModal && <VerificationModal phoneVerified={data.phone_verified} onClose={() => setShowModal(false)} onDone={() => { setShowModal(false); load(); }} />}
    </div>
  );
}
